"use client";

import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@ocr-monorepo/ui/components/dialog";
import { Button } from "@ocr-monorepo/ui/components/button";
import { Progress } from "@ocr-monorepo/ui/components/progress";
import { ScrollArea } from "@ocr-monorepo/ui/components/scroll-area";
import {
  File,
  ImageIcon,
  FileText,
  FileCode,
  Music,
  Video,
  Package,
  AlertCircle,
} from "lucide-react";
import { formatFileSize } from "../lib/utils";
import { Checkbox } from "@ocr-monorepo/ui/components/checkbox";
import Uppy, { UppyFile, Meta, Body } from "@uppy/core";
import { useUppyState, useUppyEvent } from "@uppy/react";
import UploadButton from "./upload-button";
import { FileMeta } from "@/types/uppy";
export type FileInfo = {
  id: string;
  name: string;
  size: number;
  type: string;
  progress: number;
  status: "uploading" | "complete" | "error";
  url?: string;
};

interface FileViewerDialogProps {
  open: boolean;
  uppy: Uppy<FileMeta, Body>;
  doUpload: (selectedFiles: string[]) => void;
  onOpenChange: (open: boolean) => void;
  files: UppyFile<FileMeta, Body>[];

  onCancelAll: () => void;
}
export function FileViewerDialog({
  open,
  uppy,
  doUpload,
  onOpenChange,
  files,

  onCancelAll,
}: FileViewerDialogProps) {
  const [selectedFile, setSelectedFile] = useState<UppyFile<
    FileMeta,
    Body
  > | null>(null);
  const [selectedFiles, setSelectedFiles] = useState<Set<string>>(new Set());
  const [isCompleted, setIsCompleted] = useState<boolean>();
  const [hasErrors, setHasErrors] = useState<boolean>();
  useUppyEvent(uppy, "complete", () => {
    setIsCompleted(isCompleted);
  });

  const onDoRemoveFile = (ids: Set<string>) => {
    const array = Array.from(ids.values());
    console.log(array);
    uppy.removeFiles(array);
    const newSelectedFiles = new Set(selectedFiles);
    for (const fileId of ids) {
      if (newSelectedFiles.has(fileId)) {
        newSelectedFiles.delete(fileId);
      }
    }

    setSelectedFiles(newSelectedFiles);
  };
  useUppyEvent(uppy, "upload-error", () => {
    setHasErrors(true);
  });

  const totalProgress = useUppyState(uppy, (state) => state.totalProgress);

  const getFileIcon = (fileType: string) => {
    if (fileType.startsWith("image/"))
      return <ImageIcon className="h-5 w-5 text-blue-500" />;
    if (fileType.startsWith("text/"))
      return <FileText className="h-5 w-5 text-yellow-500" />;
    if (fileType.includes("pdf"))
      return <FileText className="h-5 w-5 text-red-500" />;
    if (
      fileType.includes("javascript") ||
      fileType.includes("json") ||
      fileType.includes("html") ||
      fileType.includes("css")
    )
      return <FileCode className="h-5 w-5 text-green-500" />;
    if (fileType.startsWith("audio/"))
      return <Music className="h-5 w-5 text-purple-500" />;
    if (fileType.startsWith("video/"))
      return <Video className="h-5 w-5 text-pink-500" />;
    if (
      fileType.includes("zip") ||
      fileType.includes("rar") ||
      fileType.includes("tar") ||
      fileType.includes("gz")
    )
      return <Package className="h-5 w-5 text-orange-500" />;
    return <File className="h-5 w-5 text-gray-500" />;
  };

  const toggleFileSelection = (fileId: string) => {
    const newSelectedFiles = new Set(selectedFiles);
    if (newSelectedFiles.has(fileId)) {
      newSelectedFiles.delete(fileId);
    } else {
      newSelectedFiles.add(fileId);
    }
    setSelectedFiles(newSelectedFiles);
  };
  const toggleAllFiles = () => {
    if (selectedFiles.size === files.length) {
      // Deselect all
      setSelectedFiles(new Set());
    } else {
      // Select all
      setSelectedFiles(new Set(files.values().map((file) => file.id)));
    }
  };

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const filesArray = event.target.files ? Array.from(event.target.files) : [];
    filesArray.forEach((file) => {
      uppy.addFile(file);
    });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[800px]">
        <DialogHeader>
          <DialogTitle>File Upload</DialogTitle>
          <DialogDescription>
            {files.length} file{files.length !== 1 ? "s" : ""} being uploaded
          </DialogDescription>
        </DialogHeader>
        <div className="flex gap-4">
          <UploadButton handleInputChange={handleInputChange} />
          {selectedFiles.size > 0 && (
            <Button
              variant={"destructive"}
              onClick={() => {
                onDoRemoveFile(selectedFiles);
              }}
              disabled={selectedFiles.size == 0}
              size="sm"
              className="w-auto transition-all duration-500"
            >
              Remove {selectedFiles.size} File(s)
            </Button>
          )}
        </div>
        <div className="py-4 px-6">
          <div className="mb-4">
            <div className="flex justify-between text-sm mb-1">
              <span>Total progress</span>
              <span>{totalProgress}%</span>
            </div>
            <Progress value={totalProgress} className="h-2" />
          </div>

          {/* Table Header */}
          <div className="grid grid-cols-[40px_40px_minmax(200px,1fr)_100px_100px] gap-2 px-3 py-2 border-b text-sm font-medium text-muted-foreground">
            <div className="flex items-center">
              <Checkbox
                checked={
                  selectedFiles.size > 0 && selectedFiles.size === files.length
                }
                onCheckedChange={toggleAllFiles}
                aria-label="Select all files"
              />
            </div>
            <div></div> {/* Icon column */}
            <div>Name</div>
            <div>Size</div>
            <div>Status</div>
          </div>

          <ScrollArea className="h-[300px] rounded-md">
            {files.map((file) => (
              <div
                key={file.id}
                className={`grid grid-cols-[40px_40px_minmax(200px,1fr)_100px_100px] gap-2 items-center px-3 py-2 hover:bg-muted cursor-pointer border-b ${
                  selectedFile?.id === file.id ? "bg-muted/50" : ""
                }`}
                onClick={() => setSelectedFile(file)}
              >
                <div
                  className="flex items-center"
                  onClick={(e) => e.stopPropagation()}
                >
                  <Checkbox
                    checked={selectedFiles.has(file.id)}
                    onCheckedChange={() => toggleFileSelection(file.id)}
                    aria-label={`Select ${file.name}`}
                  />
                </div>
                <div>
                  {file.error === "error" ? (
                    <AlertCircle className="h-5 w-5 text-red-500" />
                  ) : (
                    getFileIcon(file.type)
                  )}
                </div>
                <div
                  className="truncate max-w-[200px] text-sm font-medium"
                  title={file.name}
                >
                  {file.name}
                </div>
                <div className="text-xs text-muted-foreground">
                  {formatFileSize(file.size ?? file.data?.size ?? 0)}
                </div>
                <div className="text-xs">
                  {file.progress.uploadComplete ? (
                    <span className="text-green-500">Complete</span>
                  ) : file.error === "error" ? (
                    <span className="text-red-500">Failed</span>
                  ) : (
                    <div>
                      <span>{file.progress.percentage}%</span>
                      <Progress
                        value={file.progress.percentage}
                        className="h-1 mt-1"
                      />
                    </div>
                  )}
                </div>
              </div>
            ))}
          </ScrollArea>

          {selectedFile && (
            <div className="mt-4 p-2 border rounded-md">
              <div className="text-sm font-medium mb-1">File Details</div>
              <div className="grid grid-cols-2 gap-1 text-sm">
                <div className="text-muted-foreground">Name:</div>
                <div className="truncate">{selectedFile.name}</div>
                <div className="text-muted-foreground">Size:</div>
                <div>{formatFileSize(selectedFile.size ?? 0)}</div>
                <div className="text-muted-foreground">Type:</div>
                <div>{selectedFile.type}</div>
                <div className="text-muted-foreground">Status:</div>
                <div>
                  {selectedFile.progress.uploadComplete
                    ? "Complete"
                    : selectedFile.error
                      ? "Failed"
                      : "Uploading"}
                </div>
              </div>
            </div>
          )}
        </div>

        <DialogFooter className="flex justify-between sm:justify-between">
          <div></div>
          <div className="flex gap-2">
            {!isCompleted && (
              <Button variant="outline" onClick={onCancelAll}>
                Cancel All
              </Button>
            )}
            <Button
              disabled={files.length == 0}
              onClick={() => {
                doUpload(Array.from(selectedFiles.values()));
                uppy.upload().then(() => {});
                onOpenChange(false);
              }}
            >
              {isCompleted ? "Done" : "Upload"}
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
